import gsap from 'gsap'
import { Flip } from 'gsap/dist/Flip'

import {
  fadeOutAnimation,
  fadeRevealAnimation,
  getScrollTriggerOptions,
  linesExitAnimation,
  linesRevealAnimation,
  type SharedAnimation
} from '@/components/sections/animations/shared'
import { MediaQuery } from '@/constants'

import { Animation } from '@/types'

export const imageHeroRevealAnimation = ({
  node,
  context,
  reverse,
  delay = 0
}: Animation) => {
  const selector = context?.selector || gsap.utils.selector(node)

  const title = selector('[data-motion-title]')[0]
  const text = selector('[data-motion-text]')[0]
  const media = selector('[data-motion-media]')[0]
  const cta = selector('[data-motion-cta]')[0]

  const timeline = gsap.timeline({
    ...getScrollTriggerOptions(node, reverse),
    delay
  })

  let titleAnimation: SharedAnimation
  if (title) {
    titleAnimation = linesRevealAnimation(title, reverse)
    timeline.add(titleAnimation.timeline, 0)
  }

  let textAnimation: SharedAnimation
  if (text) {
    textAnimation = linesRevealAnimation(text, reverse)
    timeline.add(textAnimation.timeline, 0.1)
  }

  let mediaAnimation: SharedAnimation
  if (media) {
    mediaAnimation = fadeRevealAnimation(media)
    timeline.add(mediaAnimation.timeline, 0)
  }

  let ctaAnimation: SharedAnimation
  if (cta) {
    ctaAnimation = fadeRevealAnimation(cta)
    timeline.add(ctaAnimation.timeline, 0.3)
  }

  return () => {
    timeline.scrollTrigger?.kill()
    timeline.kill()

    titleAnimation?.revert()
    textAnimation?.revert()
    mediaAnimation?.revert()
    ctaAnimation?.revert()
  }
}

export const imageHeroTwoColAnimation = ({
  node,
  context,
  reverse,
  delay = 0
}: Animation) => {
  const mm = gsap.matchMedia(node)

  mm.add(MediaQuery.LARGE, () => {
    const selector = context?.selector || gsap.utils.selector(node)

    const title = selector('[data-motion-title]')[0]
    const text = selector('[data-motion-text]')[0]
    const media = selector('[data-motion-media]')[0]
    const cta = selector('[data-motion-cta]')[0]
    const container = selector('[data-motion-container]')[0]

    let mediaAnimation: SharedAnimation | undefined

    const clearMediaStyles = () => {
      if (!media) return

      gsap.set(media, {
        clearProps: 'position,top,left,width,height,x,y,xPercent,yPercent,scale'
      })
    }

    const buildMediaAnimation = () => {
      if (!media || !container) return undefined

      const state = Flip.getState(media, { simple: true })

      const bounds = container.getBoundingClientRect()
      const parent = media.offsetParent as HTMLElement | null

      if (parent) {
        const parentBounds = parent.getBoundingClientRect()

        gsap.set(media, {
          position: 'absolute',
          top: bounds.top - parentBounds.top,
          left: bounds.left - parentBounds.left,
          width: bounds.width,
          height: bounds.height
        })
      }

      return {
        timeline: Flip.from(state, {
          simple: true,
          duration: 1,
          ease: 'none'
        }),
        revert: () => {
          clearMediaStyles()
        }
      }
    }

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: node,
        start: 'top top',
        end: 'bottom bottom',
        invalidateOnRefresh: true,
        invalidateOnResize: true,
        once: false,
        toggleActions: `play none none reverse`,
        scrub: true,
        delay,
        onRefreshInit: () => {
          clearMediaStyles()
        },
        onRefresh: () => {
          if (!media || !container) return

          const progress = timeline.progress()

          if (mediaAnimation) {
            timeline.remove(mediaAnimation.timeline)
            mediaAnimation.timeline.kill()
            mediaAnimation.revert()
          }

          mediaAnimation = buildMediaAnimation()

          if (mediaAnimation) {
            timeline.add(mediaAnimation.timeline, 0)
          }

          timeline.invalidate()
          timeline.progress(progress)
        }
      }
    })

    let titleAnimation: SharedAnimation
    if (title) {
      titleAnimation = linesExitAnimation(title, reverse)
      timeline.add(titleAnimation.timeline, 0)
    }

    let textAnimation: SharedAnimation
    if (text) {
      textAnimation = linesExitAnimation(text, reverse)
      timeline.add(textAnimation.timeline, 0.1)
    }

    if (media && container) {
      mediaAnimation = buildMediaAnimation()

      if (mediaAnimation) {
        timeline.add(mediaAnimation.timeline, 0)
      }
    }

    let ctaAnimation: SharedAnimation
    if (cta) {
      ctaAnimation = fadeOutAnimation(cta)
      timeline.add(ctaAnimation.timeline, 0.1)
    }

    return () => {
      timeline.scrollTrigger?.kill()
      timeline.kill()

      titleAnimation?.revert()
      textAnimation?.revert()
      mediaAnimation?.timeline.kill()
      mediaAnimation?.revert()
      ctaAnimation?.revert()
    }

  })

  return () => {
    mm.revert()
  }
}